import { useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";

/** Guest preferences (brief §8) — the crew-list sheet, read live from the deal. */
type Prefs = {
  live: boolean;
  error?: string;
  state: "received" | "draft" | "issued" | "none";
  status: string | null;
  formUrl: string | null;
  sheetType: string | null;
  fields: { key: string; label: string; value: string }[];
  alsoOnForm: { flights: boolean; transfers: boolean; crew: boolean };
};
const STATE: Record<Prefs["state"], { label: string; className: string }> = {
  received: { label: "Received", className: "bg-green-100 text-green-800" },
  draft: { label: "In progress — draft", className: "bg-amber-100 text-amber-800" },
  issued: { label: "Form sent, not started", className: "bg-slate-100 text-slate-600" },
  none: { label: "No form issued", className: "bg-slate-100 text-slate-600" },
};

export function PreferencesSection({ bookingId, hasDeal }: { bookingId: string; hasDeal: boolean }) {
  const [p, setP] = useState<Prefs | null>(null);
  useEffect(() => {
    if (!hasDeal) return;
    fetch<{ data: Prefs }>(`/api/bookings/${bookingId}/preferences`).then((r: any) => setP(r.data)).catch((e: any) => setP({ live: false, error: e?.message, state: "none", status: null, formUrl: null, sheetType: null, fields: [], alsoOnForm: { flights: false, transfers: false, crew: false } }));
  }, [bookingId, hasDeal]);

  if (!hasDeal) return null;
  if (!p) return null;
  const s = STATE[p.state];
  const also = [p.alsoOnForm.crew && "crew list", p.alsoOnForm.flights && "flights", p.alsoOnForm.transfers && "transfers"].filter(Boolean) as string[];
  return (
    <Card className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Guest preferences</h3>
            {p.live ? <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", s.className)}>{s.label}</span> : <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">HubSpot unreachable</span>}
          </div>
          <p className="text-xs text-slate-500">Preference sheet from the crew-list form, read live from the deal{p.sheetType ? ` · ${p.sheetType}` : ""}</p>
        </div>
        {p.formUrl && (
          <a className="text-sm underline" href={p.formUrl} target="_blank" rel="noreferrer noopener">
            {p.state === "received" ? "Open submitted form" : "Open form"}
          </a>
        )}
      </div>
      {!p.live && p.error && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{p.error}</p>}
      {p.live && p.fields.length === 0 && <p className="text-sm text-slate-500">{p.state === "none" ? "No preference form has been issued for this deal." : "Nothing filled in yet."}</p>}
      {p.fields.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
          {p.fields.map((f) => (
            <div key={f.key} className="border-b border-slate-100 py-1">
              <dt className="text-xs text-slate-500">{f.label}</dt>
              <dd className="text-sm">{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {also.length > 0 && <p className="text-xs text-slate-400">The form also holds the {also.join(", ")} — those aren't shown here.</p>}
    </Card>
  );
}
